import React, {Component} from 'react'
import Payment from 'payment'
import PropTypes from 'prop-types'
import Cards from 'react-credit-cards'
import Grid from '@material-ui/core/Grid'
import Input from '@material-ui/core/Input'
import {withRouter} from 'react-router-dom'
import Select from '@material-ui/core/Select'
import 'react-credit-cards/es/styles-compiled.css'
import {withNamespaces, Trans} from 'react-i18next'
import {withStyles} from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import InputLabel from '@material-ui/core/InputLabel'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import {frontends, depositStatuses, depositVendors, countries} from '@bdswiss/common-enums'
import {isEmpty, values, get, omit, map, upperCase, startsWith, some, head, pickBy, filter, includes, pick, flowRight as compose, find} from 'lodash'
import SelfPostingForm from './SelfPostingForm'
import {withCreateDeposit, withCreateDepositPCT, PaymentActionButton, BillingAddressDetails} from './helpers'
import CardToken from '../../../Common/CardToken'
import {config} from '../../../../config'
import messages from '../../../../assets/messages'
import {getPlatform} from '../../../../common/utils'
import {isMobile} from '../../../../common/utils/browser'
import {yearRange, monthRange, safeParseJSON, getFormattedAmount} from '../../../../common/utils/general'
import PageSubTitle from '../../../Common/PageSubTitle'
import CheckoutProvider from './CheckoutProvider'
import {specificProviderValidations} from '../../../../common/utils/validations'

const style = (theme) => ({
  formControl: {
    margin: `${theme.spacing(1)}px 0`
  },
  cvc: {
    paddingTop: 3,
  },
  address: {
    fontWeight: 400,
    margin: '10px 0 0 0',
  },
  addCard: {
    cursor: 'pointer',
  },
  hidden: {
    display: 'none',
  },
})

function validateSpecificProviderField(provider, value, key) {
  return value && get(specificProviderValidations[provider], key)
  && !(new RegExp(specificProviderValidations[provider][key]).test(value))
}

const validators = {
  cardNumber: (value) => !value && messages.requiredField,
  name: (value) => !value && messages.requiredField,
  expiryMonth: (value, yearValue) => {
    if (!value) return messages.requiredField
    if (isNaN(Number(value))) return messages.invalidMonth
    if (Number(value) < 1 || Number(value) > 12) {
      return messages.invalidMonth
    }
    const yearNow = new Date().getFullYear()
    const monthNow = new Date().getMonth() + 1
    if (yearValue && Number(value) < monthNow && Number(yearValue) === yearNow) {
      return messages.invalidMonth
    }
  },
  expiryYear: (value) => {
    if (!value) return messages.requiredField
    if (isNaN(Number(value))) return messages.invalidYear
    const startYear = new Date().getFullYear()
    if (Number(value) < startYear) {
      return messages.invalidYear
    }
  },
  cvc: (value) => !value && messages.requiredField,
  street: (value, _, showAddressDetails, provider) => validateSpecificProviderField(provider, value, 'street') ? messages.invalidValue
    : showAddressDetails && !value && messages.requiredField,
  city: (value, _, showAddressDetails, provider) => validateSpecificProviderField(provider, value, 'city') ? messages.invalidValue
    : showAddressDetails && !value && messages.requiredField,
  zip: (value, _, showAddressDetails, provider) => validateSpecificProviderField(provider, value, 'zip') ? messages.invalidValue
    : showAddressDetails && !value && messages.requiredField,
  country: (value, _, showAddressDetails) => showAddressDetails && !value && messages.requiredField,
}

class CreditCardProvider extends Component<any,any> {
  static propTypes = {
    account: PropTypes.shape({
      id: PropTypes.number.isRequired,
      currency: PropTypes.string.isRequired,
    }).isRequired,
    amount: PropTypes.number.isRequired,
    providerProperties: PropTypes.shape({
      name: PropTypes.string.isRequired,
      provider: PropTypes.string.isRequired,
      paymentKey: PropTypes.string.isRequired,
    }).isRequired,
    onError: PropTypes.func.isRequired,
    onSubmit: PropTypes.func,
  }
  constructor(props) {
    super(props)

    const country = get(props, 'viewer.address.country')
    const provider = get(props, 'providerProperties.provider')
    const initialState = {
      form: {cardNumber: '', name: '', expiryMonth: '', expiryYear: '', cvc: '', street: '', city: '', zip: '', country: upperCase(get(props, 'viewer.address.country', ''))},
      focused: 'number',
      errors: {},
      creatingDeposit: false,
      callback: {},
      doSubmitForm: false,
      selectedCardTokenId: null,
      showAddressDetails: includes(config.cardProvider.restrictedCountryList, country) || [depositVendors.cardpay.value, depositVendors.checkout.value].includes(provider),
      showCustomCardDetails: provider !== depositVendors.checkout.value,
    }

    const cardTokens = get(props, 'viewer.paymentCardTokens', [])
    const methodCardTokens = filter(cardTokens, (t) => t.vendor === props.providerProperties.provider)
    if (!isEmpty(methodCardTokens)) {
      initialState.selectedCardTokenId = head(methodCardTokens).id
    }
    this.state = initialState
  }

  componentDidMount() {
    document.querySelector('[name="cardNumber"]') && Payment.formatCardNumber(document.querySelector('[name="cardNumber"]'))
    document.querySelector('[name="cvc"]') && Payment.formatCardCVC(document.querySelector('[name="cvc"]'))
    //@ts-ignore
    document.getElementById('cardNumber') && document.getElementById('cardNumber').focus()
  }

  handleChange(e) {
    const {form} = this.state
    const {viewer, providerProperties: {provider}} = this.props
    const country = get(viewer, 'address.country')
    const target = e.target
    const value = target.name === 'cardNumber' ? target.value.replace(/ /g, '') : target.value
    const showAddressDetails = includes(config.cardProvider.restrictedCountryList, country) ||
      (target.name === 'cardNumber'
        ? some(config.cardProvider.restrictedBinList, (bin) => startsWith(value, bin))
        : this.state.showAddressDetails
      ) || [depositVendors.cardpay.value, depositVendors.checkout.value].includes(provider)
    this.setState({
      form: {
        ...form,
        [target.name]: value,
      },
      showAddressDetails,
    }, () => this.validateFields(target.name))
  }

  validateFields(field = '') {
    const {form, showAddressDetails} = this.state
    const {providerProperties: {provider}} = this.props
    const errors = this.state.errors || {}
    if (field) {
      const validate = validators[field]
      errors[field] = validate && validate(form[field], form.expiryYear, '', provider)
    } else {
      Object.keys(omit(form, 'cardNumber')).forEach((k) => {
        const validate = validators[k]
        errors[k] = validate && validate(form[k], form.expiryYear, showAddressDetails, provider)
      })
    }
    this.setState({errors})
    return errors
  }

  formIsValid() {
    const {form, showCustomCardDetails} = this.state
    let errors = this.validateFields()
    errors = showCustomCardDetails ? errors : pick(errors, ['name', 'street', 'zip', 'city', 'country'])
    const formFields = showCustomCardDetails ? form : pick(form, ['name', 'street', 'zip', 'city', 'country'])
    return isEmpty(values(errors).filter((v) => v)) && !isEmpty(values(formFields).filter((v) => v))
  }

  doCreateDeposit(ccTempToken?) {
    const {form, cardType, showAddressDetails, selectedCardTokenId, showCustomCardDetails} = this.state
    if (!this.formIsValid() && !selectedCardTokenId) {
      return
    }

    this.setState({creatingDeposit: true})
    const {providerProperties: {provider, paymentKey}, amount, account, onError, history, locale, useVirtualPaymentPage,
      bonusAmount, bonusTerms} = this.props

    const args : any = {
      paymentKey,
      platform: getPlatform(),
      useCardFlow: showCustomCardDetails,
      cardDetails: {
        ...form,
        cardType,
        showAddressDetails,
      },
      useVirtualPaymentPage,
      bonusAmount,
      bonusTerms,
    }

    if (provider === depositVendors.safecharge.value) {
      args.safechargeVersion = 2
      args.cardDetails = {}
    }

    let variables : any = {
      accountId: account.id,
      amount,
      vendor: provider,
      frontend: frontends.web2.value,
      ccTempToken,
      args: JSON.stringify(args),
    }
    let depositFunction = this.props.createDepositRequest
    if (selectedCardTokenId) {
      depositFunction = this.props.createDepositRequestPCT
      variables = {
        accountId: account.id,
        amount,
        paymentCardTokenId: selectedCardTokenId,
        frontend: frontends.web2.value,
        args: JSON.stringify({
          paymentKey,
          platform: getPlatform(),
          useVirtualPaymentPage,
          bonusAmount,
          bonusTerms,
        }),
      }
    }

    depositFunction({variables})
      .then(({data: {newDeposit}}) => {
        const {payment, deposit} = newDeposit
        if (deposit.status !== depositStatuses.pending.key) {
          const {isPartialApproval} = safeParseJSON(get(deposit, 'payment.meta', '{}'))
          const resultStatus = deposit.status === depositStatuses.completed.key
            ? 'success' : deposit.status === depositStatuses.failed.key ? 'failed' : 'pending'
          history.push(`/transactions/${account.id}/deposit/result/${resultStatus}`, {
            depositId: deposit.id,
            isPartialApproval,
            formattedAmount: getFormattedAmount({amount: deposit.amount, currency: account.currency, locale})
          })
        } else {
          if (payment.url) {
            window.location = payment.url
          } else {
            const payload = JSON.parse(payment.payload)
            if (payload.sessionToken) {
              if (provider === depositVendors.safecharge.key) {
                this.handleSafechargeResponse(payload)
              } else {
                this.setState({formData: payload, creatingDeposit: false, doSubmitForm: true})
              }
            } else {
              this.setState({formData: payload, creatingDeposit: false, doSubmitForm: true})
            }
          }
        }
      })
      .catch((e) => {
        this.setState({creatingDeposit: false})
        onError && onError(e)
      })
  }

  handleSafechargeResponse(payload) {
    const {providerProperties, viewer, account, history} = this.props
    const {form, showAddressDetails} = this.state
    const additionalFields = safeParseJSON(providerProperties.additionalFields)
    //@ts-ignore
    const sfc = window.SafeCharge({
      env: config.common.safecharge.env,
      merchantId : additionalFields.merchantId,
      merchantSiteId : additionalFields.merchantSiteId,
    })

    const paymentOption : any = {}
    if (payload.userPaymentOptionId) {
      paymentOption.userPaymentOptionId = payload.userPaymentOptionId
    } else {
      paymentOption.card = {
        cardNumber: form.cardNumber,
        cardHolderName: form.name,
        expirationMonth: form.expiryMonth,
        expirationYear: form.expiryYear,
        CVV: form.cvc,
      }
    }

    const personalInfo = {
      email: viewer.email,
      country: form.country || get(find(countries, {key: get(viewer, 'address.country')}), 'value'),
    }

    const paymentPayload = {
      sessionToken: payload.sessionToken,
      merchantId: additionalFields.merchantId,
      merchantSiteId: additionalFields.merchantSiteId,
      clientUniqueId: payload.clientUniqueId,
      paymentOption,
      billingAddress: showAddressDetails ? {
        address: form.street,
        zip: form.zip,
        city: form.city,
        ...personalInfo
      } : personalInfo
    }


    sfc.createPayment(paymentPayload, (res) => {
      if (res.cancelled) {
        history.push(`/transactions/${account.id}/deposit/result/failed`)
      } else if (res.result === 'APPROVED') {
        history.push(`/transactions/${account.id}/deposit/result/success`)
      } else {
        history.push(`/transactions/${account.id}/deposit/result/failed`)
      }
    })
  }

  checkCardNumber() {
    const {callback: {type, isValid}, errors, form: {cardNumber}} = this.state
    if (get(type, 'issuer') === 'unknown' || !isValid) {
      const isMaestro = new RegExp('^(5[06789]|6)[0-9]{0,}$').test(cardNumber)
      if (isMaestro) {
        this.setState({
          cardType: 'maestro',
        })
      } else {
        this.setState({
          errors: {
            ...errors,
            cardNumber: messages.invalidCardNumber,
          },
        })
      }
    } else if (get(type, 'issuer') !== 'unknown' && isValid) {
      this.setState({
        cardType: get(type, 'issuer'),
        errors: omit(errors, 'cardNumber'),
      })
    }
  }

  render() {
    const {classes, viewer: {paymentCardTokens}, providerProperties: {provider}} = this.props
    const {form, creatingDeposit, formData, errors, focused, doSubmitForm, showAddressDetails,
      selectedCardTokenId, showCustomCardDetails,
    } = this.state
    const methodCardTokens = pickBy(paymentCardTokens, (t) => t.vendor === provider)

    const expiry = `${get(form, 'expiryMonth', '')}/${get(form, 'expiryYear', '')}`
    return (
      <React.Fragment>
        <SelfPostingForm formData={formData} doSubmitForm={doSubmitForm} />
        {!isEmpty(methodCardTokens) &&
          <React.Fragment>
            <Grid container direction="row" spacing={1}>
              <Grid item xs={12}>
                <PageSubTitle /*className={classes.address}*/><Trans {...messages.yourCards} /></PageSubTitle>
              </Grid>
              {map(methodCardTokens, (t) =>
                <Grid item xs={12} key={t.id}>
                  <CardToken
                    cardToken={t}
                    isSelected={selectedCardTokenId === t.id}
                    onClick={(id) => this.setState({selectedCardTokenId: id})} />
                </Grid>
              )}
            </Grid>
            <Grid container direction="row" spacing={1}>
              <Grid item xs={12}>
                <Typography
                  variant={isMobile() ? 'body1' : 'body2'}
                  color="primary"
                  onClick={() => {
                    this.setState((state) => ({
                      selectedCardTokenId: null,
                      errors: {
                        ...state.errors,
                        cardNumber: messages.invalidCardNumber,
                      },
                    }))
                  }}
                  className={classes.addCard}
                >
                  <Trans {...messages.addNewCard} />
                </Typography>
              </Grid>
            </Grid>
          </React.Fragment>
        }
        <React.Fragment>
          <Grid container
            direction="row"
            spacing={1}
            className={((isEmpty(methodCardTokens) || !selectedCardTokenId) && showCustomCardDetails) ? '' : classes.hidden}
          >
            <Grid item xs={12}>
              <Cards
                number={get(form, 'cardNumber', '')}
                name={get(form, 'name', '')}
                expiry={expiry}
                cvc={get(form, 'cvc', '').replace(/[0-9]/g, '*')}
                focused={focused}
                callback={(type, isValid) => this.setState({callback: {type, isValid}})}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl className={classes.formControl} fullWidth>
                <InputLabel htmlFor="cardNumber">
                  <Trans {...messages.cardNumber} />
                </InputLabel>
                <Input
                  id="cardNumber"
                  type="text"
                  name="cardNumber"
                  error={!!errors.cardNumber}
                  onKeyUp={(e) => this.handleChange(e)}
                  onFocus={(e) => this.setState({focused: 'number'})}
                  onBlur={() => this.checkCardNumber()}
                  inputProps={{pattern: '[0-9]*', inputMode:'numeric', autoComplete: 'off'}}
                />
                {
                  errors.cardNumber &&
                  <FormHelperText className={classes.error}><Trans {...errors.cardNumber} /></FormHelperText>
                }
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl className={classes.formControl} fullWidth>
                <InputLabel htmlFor="name">
                  <Trans {...messages.fullName} />
                </InputLabel>
                <Input
                  id="name"
                  type="text"
                  name="name"
                  error={!!errors.name}
                  onChange={(e) => this.handleChange(e)}
                  onFocus={(e) => this.setState({focused: e.target.name})}
                  inputProps={{autoComplete: 'off'}}
                />
                {
                  errors.name &&
                  <FormHelperText className={classes.error}><Trans {...errors.name} /></FormHelperText>
                }
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl className={classes.formControl} fullWidth>
                <InputLabel htmlFor="expiryMonth">
                  <Trans {...messages.expiryMonth} />
                </InputLabel>
                <Select
                  native
                  id="expiryMonth"
                  name="expiryMonth"
                  type="text"
                  value={get(form, 'expiryMonth', '')}
                  onChange={(e) => this.handleChange(e)}
                  inputProps={{name: 'expiryMonth'}}
                  onFocus={(e) => this.setState({focused: e.target.name})}
                >
                  <option disabled key="empty" value="" />
                  {map(monthRange, (m) => (<option key={m} value={m}>{m}</option>))}
                </Select>
                {
                  errors.expiryMonth &&
                  <FormHelperText className={classes.error}><Trans {...errors.expiryMonth} /></FormHelperText>
                }
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl className={classes.formControl} fullWidth>
                <InputLabel htmlFor="expiryYear">
                  <Trans {...messages.expiryYear} />
                </InputLabel>
                <Select
                  native
                  id="expiryYear"
                  name="expiryYear"
                  type="text"
                  value={get(form, 'expiryYear', '')}
                  onChange={(e) => this.handleChange(e)}
                  inputProps={{name: 'expiryYear'}}
                  onFocus={(e) => this.setState({focused: e.target.name})}
                >
                  <option disabled key="empty" value="" />
                  {map(yearRange, (m) => (<option key={m} value={m}>{m}</option>))}
                </Select>
                {
                  errors.expiryYear &&
                  <FormHelperText className={classes.error}><Trans {...errors.expiryYear} /></FormHelperText>
                }
              </FormControl>
            </Grid>
            <Grid item xs={2} sm={4}>
              <FormControl className={classes.formControl} fullWidth>
                <InputLabel htmlFor="cvc">
                  <Trans {...messages.cvv} />
                </InputLabel>
                <Input
                  id="cvc"
                  type="text"
                  className={classes.cvc}
                  name="cvc"
                  error={!!errors.cvc}
                  onChange={(e) => {
                    //@ts-ignore
                    document.getElementById('cvc').type = 'password'
                    this.handleChange(e)
                  }}
                  onFocus={(e) => this.setState({focused: e.target.name})}
                  inputProps={{pattern: '[0-9]*', maxLength: 4, inputMode:'numeric', autoComplete: 'off', className: classes.cvc}}
                />
                {
                  errors.cvc &&
                  <FormHelperText className={classes.error}><Trans {...errors.cvc} /></FormHelperText>
                }
              </FormControl>
            </Grid>
            {showAddressDetails && <BillingAddressDetails
              errors={errors}
              form={form}
              handleChange={(e) => this.handleChange(e)}
              onCardFocus={() => this.setState({focused: 'number'})}
            />}
          </Grid>
          {!((isEmpty(methodCardTokens) || !selectedCardTokenId) && !showCustomCardDetails) && <PaymentActionButton
            loading={creatingDeposit}
            disable={false}
            onClick={() => this.doCreateDeposit()}
          />}
        </React.Fragment>
        {((isEmpty(methodCardTokens) || !selectedCardTokenId) && !showCustomCardDetails) && <CheckoutProvider
          doCreateDeposit={(token) => this.doCreateDeposit(token)}
          creatingDeposit={creatingDeposit}
          selectedCardTokenId={selectedCardTokenId}
          errors={errors}
          form={form}
          handleChange={(e) => this.handleChange(e)}
          onCardFocus={() => this.setState({focused: 'number'})}
          formIsValid={() => this.formIsValid()}
        />}
      </React.Fragment>
    )
  }
}


export default compose(
  withStyles(style),
  withNamespaces(),
  withRouter,
  withCreateDeposit,
  withCreateDepositPCT,
)(CreditCardProvider)
